import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    X, Download, CheckSquare, Square, FileSpreadsheet, 
    ChevronRight, ChevronDown, Filter, PlusCircle, 
    Trash2, ArrowRight, Calendar, DollarSign, Gavel, Users, 
    Briefcase, Info, HelpCircle, RefreshCw, Lightbulb
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ConfirmationModal from './ConfirmationModal'; 
import api from '../services/api.js';
import { ALL_FIELDS_FLAT, FIELD_GROUPS, SAMPLE_ORDER } from '../store/exportReportConstants.js';
import {
    resetExportReport,
    selectExportReport,
    setExpandedGroups,
    setFilterIntent,
    setFoundRecords,
    setIsFilterDirty,
    setLastFilterSummary,
    setLoading,
    setModalConfig,
    setPotentialMatches,
    setProvidersLoading,
    setRawSearchResults,
    setSelectedFields,
    setSelectedRecordIds,
    setShowGuide,
    setShowMatchSelection,
    setStep,
    setTempSelectedMatches,
    setUniqueProviders,
    updateActiveSections,
    updateFilters,
} from '../store/exportReportSlice.js';

const ExportReportModal = ({ isOpen, onClose, collectors = [] }) => {
    const dispatch = useDispatch();
    const {
        step,
        showGuide,
        showMatchSelection,
        potentialMatches,
        rawSearchResults,
        tempSelectedMatches,
        filters,
        activeSections,
        isFilterDirty,
        uniqueProviders,
        providersLoading,
        filterIntent,
        loading,
        foundRecords,
        lastFilterSummary,
        selectedFields,
        selectedRecordIds,
        expandedGroups,
        modalConfig,
    } = useSelector(selectExportReport);

    // --- EFFECT: Monitor Filter Changes to Set Dirty State ---
    useEffect(() => {
        // Only mark dirty if we have records already
        // This prevents the user from clicking 'Download' if they changed the filter but didn't click Append/Replace
        if (foundRecords.length > 0) {
            dispatch(setIsFilterDirty(true));
        }
    }, [filters, activeSections, foundRecords.length, dispatch]);

    // --- NEW: Fetch Unique Providers on Mount ---
    useEffect(() => {
        if (isOpen) {
             // --- UPDATED: Force Select All Fields whenever the modal opens ---
            dispatch(setSelectedFields(ALL_FIELDS_FLAT.map(f => f.key)));
            dispatch(setExpandedGroups(Object.keys(FIELD_GROUPS)));
            const fetchProviders = async () => {
                dispatch(setProvidersLoading(true));
                try {
                    // FIX: Added '/records' prefix to match backend controller
                    const response = await api.get('/records/unique-providers');                    
                    let fetchedProviders = [];
                    if (Array.isArray(response.data)) {
                        fetchedProviders = response.data;
                    } else if (response.data && Array.isArray(response.data.data)) {
                        fetchedProviders = response.data.data;
                    }
                    // Filter out empty strings or nulls just in case
                    const cleanProviders = fetchedProviders.filter(p => p && typeof p === 'string' && p.trim() !== '');
                    dispatch(setUniqueProviders(cleanProviders.sort()));
                } catch (error) {
                    console.error("Failed to fetch unique providers list", error);
                } finally {
                    dispatch(setProvidersLoading(false));
                }
            };
            fetchProviders();
        }
    }, [isOpen, dispatch]);

    if (!isOpen) return null;

    // --- HELPER: ADJUST END DATE (Like HearingCalendar) ---
    const getAdjustedEndDate = (dateStr) => {
        if (!dateStr) return '';
        // Treat input string as UTC midnight to avoid timezone shifting
        const endDateObj = new Date(dateStr + 'T00:00:00Z');
        // Add 1 day
        endDateObj.setUTCDate(endDateObj.getUTCDate() + 1);
        // Return YYYY-MM-DD
        return endDateObj.toISOString().split('T')[0];
    };

    // --- RESET LOGIC ---
    const performResetAndClose = () => {
        onClose();
        setTimeout(() => {
            dispatch(resetExportReport());
        }, 150); 
    };

    const handleCloseClick = () => {
        if (step === 2 || foundRecords.length > 0) {
            dispatch(setModalConfig({
                isOpen: true,
                title: 'Cancel Export?',
                message: "Are you sure you want to cancel? Your current selection will be lost.",
                confirmText: 'Discard',
                isDanger: true,
                actionType: 'close'
            }));
        } else {
            performResetAndClose();
        }
    };

    const toggleGuide = () => {
        dispatch(setShowGuide(!showGuide));
    };

    // --- HELPER: Generate Filter Summary String ---
    const generateSummary = (actionType = 'Search') => {
        let criteria = '';
        
        const activeParts = [];
        if(activeSections.provider && filters.provider) activeParts.push(`Provider "${filters.provider}"`);
        if(activeSections.collector && filters.collector) {
            const col = collectors.find(c => c._id === filters.collector);
            activeParts.push(`Collector "${col ? col.username : 'ID'}"`);
        }
        if(activeSections.hearing) activeParts.push(`Hearing (${filters.hearingStartDate || '?'} - ${filters.hearingEndDate || '?'})`);
        if(filters.isSettled) activeParts.push("Settled Only");
        if(filters.isOffer) activeParts.push("With Offer");
        criteria = activeParts.join(", ") || "Batch Filter";
        
        return `${actionType}: ${criteria}`;
    };

    // --- CORE LOGIC: PRE-FILTER CHECK (Broad) ---
    const checkRecordMatchesFilters = (r) => {
        // 1. Provider Check (Loose Include for Initial Fetch - Strict Check happens in Modal)
        if (activeSections.provider && filters.provider) {
            const searchStr = filters.provider.toLowerCase().trim();
            if (!r.provider || !r.provider.toLowerCase().includes(searchStr)) return false;
        }

        // 2. Collector Check (Strict AND)
        if (activeSections.collector && filters.collector) {
            const rColId = r.assignedCollector?._id || r.assignedCollector;
            if (rColId !== filters.collector) return false;
        }

        // 3. Hearing Check (Strict AND)
        if (activeSections.hearing) {
             if (!r.hearingDate) return false;
             // --- FIX: USE STRING SPLIT (No timezone shift) ---
             const rDate = String(r.hearingDate).split('T')[0];
             
             if (filters.hearingStartDate && rDate < filters.hearingStartDate) return false;
             if (filters.hearingEndDate && rDate > filters.hearingEndDate) return false;
        }

        // 4. Settled Check (Strict AND)
        if (filters.isSettled) {
            const status = r.caseStatus ? r.caseStatus.toUpperCase().trim() : '';
            if (status !== 'SETTLED') return false;
        }

        // 5. Offer Check (Strict AND)
        if (filters.isOffer) {
            const hasOfferStatus = r.comments?.some(c => c.status === 'offer');
            const hasOfferAmount = r.comments?.some(c => c.offerAmount && c.offerAmount > 0);
            if (!hasOfferStatus && !hasOfferAmount) return false;
        }

        return true;
    };

    // --- COMMON HELPERS ---
    const handleConfirmMatches = (goToNext = false) => {
        if (tempSelectedMatches.length === 0) return;

        // Strict Filtering: Only get records that EXACTLY match the selected names
        let newRecords = rawSearchResults.filter(r => {
            if (!r.provider) return false;
            return tempSelectedMatches.some(selected => selected.toLowerCase() === r.provider.toLowerCase());
        });
        
        addRecordsToBasket(newRecords, goToNext);
        dispatch(setShowMatchSelection(false));
        dispatch(setPotentialMatches([]));
    };

    const toggleMatchSelection = (matchValue) => {
        dispatch(
            setTempSelectedMatches(
                tempSelectedMatches.includes(matchValue)
                    ? tempSelectedMatches.filter(m => m !== matchValue)
                    : [...tempSelectedMatches, matchValue]
            )
        );
    };

    const addRecordsToBasket = (newRecords, goToNext) => {
        // --- NEW LOGIC: HANDLING REPLACE VS APPEND ---
        if (filterIntent === 'replace') {
            // If REPLACE intent: Wipe old records, set new ones
            dispatch(setFoundRecords(newRecords));
            dispatch(setLastFilterSummary(generateSummary('New Search')));
        } else {
            // APPEND intent
            const existingIds = new Set(foundRecords.map(r => r._id));
            const uniqueNew = newRecords.filter(r => !existingIds.has(r._id));
            
            if (uniqueNew.length === 0 && newRecords.length > 0) {
                 showErrorAlert("Duplicate Data", "These records are already in your selection.");
                 // Even if duplicate, we consider the search 'done' so reset dirty
                 dispatch(setIsFilterDirty(false));
                 return;
            }
            
            const updatedRecords = [...foundRecords, ...uniqueNew];
            dispatch(setFoundRecords(updatedRecords));
            
            dispatch(
                setLastFilterSummary(
                    lastFilterSummary
                        ? lastFilterSummary + " + " + generateSummary('Appended')
                        : generateSummary('Filtered')
                )
            );
        }
        
        // --- KEY CHANGE: A successful search (Append or Replace) clears the dirty state ---
        dispatch(setIsFilterDirty(false));
        
        if (goToNext) {
            // If replacing, select all new ids. If appending, select new + old (or keep previous logic)
            // Here we assume select all visible for simplicity
            const currentRecords = (filterIntent === 'replace') ? newRecords : [...foundRecords, ...newRecords];
            dispatch(setSelectedRecordIds(currentRecords.map(r => r._id)));
            dispatch(setStep(2));
        }
    };

    // --- LOGIC 3: ADVANCED BATCH FILTER ---
    const handleAdvancedFilter = async (intent = 'append') => {
        // SET INTENT IMMEDIATELY
        dispatch(setFilterIntent(intent));

        // --- 1. MANDATORY FIELD VALIDATION ---
        if (activeSections.provider && !filters.provider.trim()) {
            showErrorAlert("Provider Required", "You checked 'Provider Level' but left the value empty.");
            return;
        }
        if (activeSections.hearing && (!filters.hearingStartDate || !filters.hearingEndDate)) {
            showErrorAlert("Dates Required", "You checked 'Hearing Report'. Both Start and End dates are required.");
            return;
        }
        if (activeSections.collector && !filters.collector) {
             showErrorAlert("Collector Required", "You checked 'Collector' but didn't select one.");
             return;
        }

        dispatch(setLoading(true));
        dispatch(setShowMatchSelection(false));
        dispatch(setPotentialMatches([]));

        try {
            const params = { limit: 2000 };
            
            // 2. Prepare Params
            if (activeSections.provider && filters.provider) params.provider = filters.provider;
            if (activeSections.collector && filters.collector) params.collectorId = filters.collector;
            if (activeSections.hearing) {
                params.hearingStartDate = filters.hearingStartDate;
                // --- FIX: Use Adjusted Date for API call to get all records for the end day ---
                params.hearingEndDate = getAdjustedEndDate(filters.hearingEndDate);
            }

            const isAnyActive = activeSections.provider || activeSections.collector || activeSections.hearing || filters.isSettled || filters.isOffer;
            if (!isAnyActive) {
                showErrorAlert('Filter Required', "Please check the box for at least one filter criteria.");
                dispatch(setLoading(false));
                return;
            }

            // 3. Fetch New Records
            const response = await api.get('/records', { params });
            let serverRecords = response.data.data || [];

            // 4. Client-Side Broad Validation
            let validNewRecords = serverRecords.filter(r => checkRecordMatchesFilters(r));

            if (validNewRecords.length === 0) {
                showNoRecordsAlert();
                dispatch(setLoading(false));
                return;
            }

            // 5. Provider Smart Match & Strictness Check
            if (activeSections.provider && filters.provider) {
                const searchStr = filters.provider.toLowerCase().trim();
                const matchingNames = new Set();
                
                validNewRecords.forEach(r => {
                    if (r.provider && r.provider.toLowerCase().includes(searchStr)) {
                        matchingNames.add(r.provider);
                    }
                });
                
                const uniqueMatches = Array.from(matchingNames).sort();
                
                // --- UPDATE: INTELLIGENT BYPASS FOR EXACT SELECTION ---
                // If the user selected "CCMG" and the results contain "CCMG" (exact),
                // we assume that is what they wanted, even if "C-ccmg" exists.
                const exactMatchName = uniqueMatches.find(name => name.toLowerCase() === searchStr);
                
                if (exactMatchName) {
                    // Exact match found! Prioritize it and bypass modal.
                    validNewRecords = validNewRecords.filter(r => r.provider && r.provider.toLowerCase() === searchStr);
                    // We don't return here, we fall through to the ACTION HANDLER below with the filtered records
                } else {
                    // Standard logic: If no exact match found, check for multiple matches or fuzzy match
                    const isSingleResultExact = uniqueMatches.length === 1 && uniqueMatches[0].toLowerCase() === searchStr;

                    if (uniqueMatches.length > 1 || (uniqueMatches.length === 1 && !isSingleResultExact)) {
                           dispatch(setRawSearchResults(validNewRecords));
                           dispatch(setPotentialMatches(uniqueMatches));
                           dispatch(setTempSelectedMatches(uniqueMatches));
                           dispatch(setShowMatchSelection(true));
                           dispatch(setLoading(false));
                           return; // Wait for handleConfirmMatches
                    }
                }
            }

            // 6. ACTION HANDLER (No ambiguity found, or ambiguity resolved via exact match)
            if (intent === 'replace') {
                dispatch(setFoundRecords(validNewRecords));
                dispatch(setLastFilterSummary(generateSummary('New Search')));
            } else {
                const existingIds = new Set(foundRecords.map(r => r._id));
                const uniqueNew = validNewRecords.filter(r => !existingIds.has(r._id));
                if (uniqueNew.length === 0) {
                    showErrorAlert("No New Data", "Records matching these filters are already in your list.");
                } else {
                    dispatch(setFoundRecords([...foundRecords, ...uniqueNew]));
                    dispatch(
                        setLastFilterSummary(
                            lastFilterSummary
                                ? lastFilterSummary + " + " + generateSummary('Appended')
                                : generateSummary('Filtered')
                        )
                    );
                }
            }
            // Success - Reset Dirty State
            dispatch(setIsFilterDirty(false));

        } catch (error) {
            console.error("Filter error:", error);
            showErrorAlert('Error', 'Failed to fetch advanced filter results.');
        } finally {
            dispatch(setLoading(false));
        }
    };

    const showNoRecordsAlert = () => showErrorAlert('No Records', `No records found matching strict criteria.`);
    const showErrorAlert = (title, message) => {
        dispatch(setModalConfig({ isOpen: true, title, message, confirmText: 'OK', isDanger: false, actionType: null }));
    };

    const handleModalConfirm = () => {
        const actionType = modalConfig.actionType;
        dispatch(setModalConfig({ isOpen: false }));

        if (actionType === 'close') {
            performResetAndClose();
            return;
        }

        if (actionType === 'clearAll') {
            dispatch(setFoundRecords([]));
            dispatch(setLastFilterSummary(''));
            dispatch(setIsFilterDirty(false));
            return;
        }

        if (actionType === 'revertUnsaved') {
            dispatch(setIsFilterDirty(false));
            dispatch(setSelectedRecordIds(foundRecords.map(r => r._id)));
            dispatch(setStep(2));
        }
    };

   // --- EXPORT LOGIC ---
    const handleExport = () => {
        if (selectedRecordIds.length === 0 || selectedFields.length === 0) return;
        const recordsToExport = foundRecords.filter(r => selectedRecordIds.includes(r._id));
        
        // 1. Initialize columns based on user selection
        let exportColumns = [];
        
        SAMPLE_ORDER.forEach(key => {
            if (selectedFields.includes(key)) {
                const def = ALL_FIELDS_FLAT.find(f => f.key === key);
                if (def) exportColumns.push(def);
            }
        });
        ALL_FIELDS_FLAT.forEach(def => {
            if (selectedFields.includes(def.key) && !SAMPLE_ORDER.includes(def.key)) {
                exportColumns.push(def);
            }
        });

        // --- NEW CHANGE: HANDLE OFFER COLUMN POSITIONING & VISIBILITY ---
        // Check if we are in an "Offer" context (Advanced Filter 'isOffer')
        const isOfferContext = filters.isOffer;

        if (isOfferContext) {
            // CASE 1: Offer Option Selected -> Show it at the end
            // Remove 'offerAmount' from its current position (if it exists) to prevent duplicates
            exportColumns = exportColumns.filter(col => col.key !== 'offerAmount');

            // Find the definition and force add it to the VERY END of the list
            const offerFieldDef = ALL_FIELDS_FLAT.find(f => f.key === 'offerAmount');
            if (offerFieldDef) {
                exportColumns.push(offerFieldDef);
            }
        } else {
            // CASE 2: Offer Option NOT Selected -> Hide it completely
            // Even if 'offerAmount' is in selectedFields, we remove it from the final export
            exportColumns = exportColumns.filter(col => col.key !== 'offerAmount');
        }
        // --------------------------------------------------

        const excelData = recordsToExport.map(record => {
            const row = {};
            exportColumns.forEach(fieldDef => {
                let value = record[fieldDef.key];

                // --- FIXED: OFFER AMOUNT LOGIC ---
                if (fieldDef.key === 'offerAmount') {
                    // Force extraction of offer amount if available in comments
                    // Ensure comments exist and filter specifically for offerAmount > 0
                    const commentsWithOffers = record.comments?.filter(c => c.offerAmount && Number(c.offerAmount) > 0);
                    
                    if (commentsWithOffers && commentsWithOffers.length > 0) {
                        // Sort by date descending (newest first)
                        commentsWithOffers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                        value = commentsWithOffers[0].offerAmount; 
                    } else {
                        value = ''; 
                    }
                }
                else if (fieldDef.key === 'assignedCollector') value = value?.username || 'Unassigned';
                else if (Array.isArray(value)) value = value.map(v => v.value).join(', ');
                else if (['hearingDate', 'dob', 'solDate', 'caseDate', 'fds', 'lds'].includes(fieldDef.key) && value) {
                      value = new Date(value).toLocaleDateString(undefined, { timeZone: 'UTC' });
                }
                
                row[fieldDef.label] = value;
            });
            return row;
        });
        
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        XLSX.writeFile(wb, `Export_${new Date().toISOString().slice(0,10)}.xlsx`);
        performResetAndClose(); 
    };
    const handleClearAll = () => {
        dispatch(setModalConfig({
            isOpen: true,
            title: 'Clear Selection',
            message: 'Are you sure you want to clear all found records?',
            confirmText: 'Clear All',
            isDanger: true,
            actionType: 'clearAll'
        }));
    };

    // --- UI HELPERS ---
    const proceedToStep2 = () => { 
        // --- NEW LOGIC: PREVENT STEP CHANGE IF FILTERS ARE DIRTY ---
        
        if (isFilterDirty) {
            dispatch(setModalConfig({
               isOpen: true,
                title: 'Unsaved Changes',
                message:
                    'You have modified your search inputs but have not applied them yet. ' +
                    'Choose "Revert & Continue" to keep previous results, or Cancel to apply changes.',
                confirmText: 'Revert & Continue',
                isDanger: true,
                actionType: 'revertUnsaved',
            }));
            return;
        }

        dispatch(setSelectedRecordIds(foundRecords.map(r => r._id)));
        dispatch(setStep(2));
    };

    const toggleField = (key) =>
        dispatch(
            setSelectedFields(
                selectedFields.includes(key)
                    ? selectedFields.filter(k => k !== key)
                    : [...selectedFields, key]
            )
        );
    const toggleGroupFields = (groupName) => {
        const groupKeys = FIELD_GROUPS[groupName].map(f => f.key);
        const allSelected = groupKeys.every(k => selectedFields.includes(k));
        dispatch(
            setSelectedFields(
                allSelected
                    ? selectedFields.filter(k => !groupKeys.includes(k))
                    : [...new Set([...selectedFields, ...groupKeys])]
            )
        );
    };
    const toggleAllFields = () =>
        dispatch(
            setSelectedFields(
                selectedFields.length === ALL_FIELDS_FLAT.length ? [] : ALL_FIELDS_FLAT.map(f => f.key)
            )
        );
    const toggleRecord = (id) =>
        dispatch(
            setSelectedRecordIds(
                selectedRecordIds.includes(id)
                    ? selectedRecordIds.filter(i => i !== id)
                    : [...selectedRecordIds, id]
            )
        );
    const toggleAllRecords = () =>
        dispatch(
            setSelectedRecordIds(
                selectedRecordIds.length === foundRecords.length ? [] : foundRecords.map(r => r._id)
            )
        );
    const toggleExpandGroup = (group) =>
        dispatch(
            setExpandedGroups(
                expandedGroups.includes(group)
                    ? expandedGroups.filter(g => g !== group)
                    : [...expandedGroups, group]
            )
        );

    const uniqueCollectors = collectors.filter(c => c && c.username && c._id && c.username !== 'unassigned');

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-sm">
            <ConfirmationModal 
                isOpen={modalConfig.isOpen} onClose={() => dispatch(setModalConfig({ isOpen: false }))}
                onConfirm={handleModalConfirm} title={modalConfig.title} message={modalConfig.message}
                confirmText={modalConfig.confirmText} isDanger={modalConfig.isDanger}
            />

            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] h-full md:h-[90vh] flex flex-col overflow-hidden border border-gray-100 relative">
                
                {/* --- GUIDE OVERLAY UI --- */}
                {showGuide && (
                    <div className="absolute inset-x-0 top-[60px] z-[60] bg-white border-b shadow-xl p-6 animate-in slide-in-from-top-5 duration-300 max-h-[80vh] overflow-y-auto">
                        <div className="max-w-4xl mx-auto">
                            <div className="flex justify-between items-start mb-6">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                        <Lightbulb className="w-5 h-5 text-yellow-500 fill-current" /> 
                                        How to use Export Search
                                    </h3>
                                    <p className="text-gray-500 text-sm mt-1">Use the advanced filter to build your list.</p>
                                </div>
                                <button onClick={() => dispatch(setShowGuide(false))} className="p-2 hover:bg-gray-100 rounded-full text-gray-400">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="grid md:grid-cols-1 gap-6">
                                {/* Advanced Filter Guide */}
                                <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100">
                                    <div className="flex items-center gap-2 mb-3 text-indigo-800 font-bold">
                                        <Filter className="w-5 h-5" />
                                        <h4>Advanced Batch Filter</h4>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-3">Best for complex data mining and building large lists.</p>
                                    <ul className="space-y-2 text-sm text-gray-700">
                                        <li className="flex gap-2"><CheckSquare className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" /> <span>Combine multiple criteria (e.g., Specific Collector AND Date Range).</span></li>
                                        <li className="flex gap-2"><PlusCircle className="w-4 h-4 text-gray-600 shrink-0 mt-0.5" /> <span><b>Append Results:</b> Adds the new matches to your existing list.</span></li>
                                        <li className="flex gap-2"><RefreshCw className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" /> <span><b>New Search (Replace):</b> Clears your current list and starts fresh with these results.</span></li>
                                    </ul>
                                </div>
                            </div>
                            <div className="mt-6 text-center">
                                <button onClick={() => dispatch(setShowGuide(false))} className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium">Got it</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- MATCH SELECTION OVERLAY --- */}
                {showMatchSelection && (
                    <div className="absolute inset-0 bg-white z-50 flex flex-col p-4 sm:p-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
                          <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Found {potentialMatches.length} Matches</h3>
                            <p className="text-sm sm:text-base text-gray-500 mb-6">
                                Based on your Advanced Filter, we found these providers matching your input. Please select exact matches:
                            </p>
                            <div className="flex items-center gap-2 mb-3">
                                <button onClick={() => dispatch(setTempSelectedMatches(potentialMatches))} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 font-medium">Select All</button>
                                <button onClick={() => dispatch(setTempSelectedMatches([]))} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded hover:bg-gray-200 font-medium">Deselect All</button>
                            </div>
                            <div className="flex-1 overflow-y-auto border border-gray-200 rounded-xl shadow-sm bg-gray-50 p-2 custom-scrollbar">
                                {potentialMatches.map((match, idx) => (
                                    <button key={idx} onClick={() => toggleMatchSelection(match)} className={`w-full text-left px-4 py-3 mb-1 rounded-lg border transition-all flex items-center gap-3 group ${tempSelectedMatches.includes(match) ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-white border-transparent hover:bg-gray-50'}`}>
                                            <div className={`p-1 rounded-md shrink-0 ${tempSelectedMatches.includes(match) ? 'bg-blue-600' : 'bg-gray-200'}`}>
                                                {tempSelectedMatches.includes(match) ? <CheckSquare className="w-4 h-4 text-white" /> : <Square className="w-4 h-4 text-white" />}
                                            </div>
                                            <span className="font-medium truncate">{match}</span>
                                    </button>
                                ))}
                            </div>
                            <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                                <button onClick={() => dispatch(setShowMatchSelection(false))} className="px-4 py-2 text-gray-500 hover:text-gray-800 font-medium">Cancel</button>
                                <button onClick={() => handleConfirmMatches(false)} disabled={tempSelectedMatches.length === 0} className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 flex items-center justify-center gap-2 disabled:opacity-50">
                                     {filterIntent === 'replace' ? 'Confirm & Replace' : 'Confirm & Append'} <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                          </div>
                    </div>
                )}

                {/* HEADER */}
                <div className="flex justify-between items-center px-4 py-3 sm:px-6 sm:py-4 border-b bg-white">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg shrink-0">
                            <FileSpreadsheet className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-700" />
                        </div>
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800">Export Report & Analytics</h2>
                            <p className="text-xs text-gray-500 hidden sm:block">{step === 1 ? 'Step 1: Search & Filter Data' : 'Step 2: Review & Download'}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                         <button onClick={toggleGuide} className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border transition-colors ${showGuide ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                            <HelpCircle className="w-4 h-4" /> {showGuide ? 'Hide Guide' : 'Show Guide'}
                         </button>
                        <button onClick={handleCloseClick} className="text-gray-400 hover:text-gray-600 bg-gray-100 p-2 rounded-full hover:bg-gray-200">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden bg-gray-50 relative">
                    {step === 1 ? (
                        <div className="flex flex-col h-full overflow-hidden">
                            {/* ADVANCED FILTER ONLY - REMOVED TABS */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-8">
                                <div className="max-w-4xl mx-auto w-full">
                                    
                                    {/* --- ADVANCED FILTER UI --- */}
                                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="h-8 w-1 bg-indigo-500 rounded-full"></div>
                                            <h3 className="text-lg font-bold text-gray-800">Generate Report </h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            
                                            {/* 1. Provider Level (Checkboxed) */}
                                            <div className={`space-y-2 transition-all duration-300 ${activeSections.provider ? 'opacity-100' : 'opacity-40 grayscale'}`}>
                                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2 cursor-pointer select-none">
                                                    <input type="checkbox" checked={activeSections.provider} onChange={() => dispatch(updateActiveSections({ provider: !activeSections.provider }))} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" />
                                                    <Briefcase className="w-3 h-3" /> 1. Provider Level
                                                </label>
                                                <select disabled={!activeSections.provider} value={filters.provider} onChange={(e) => dispatch(updateFilters({ provider: e.target.value }))} className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed">
                                                    <option value="">Select Provider...</option>
                                                    {providersLoading ? (
                                                        <option disabled>Loading providers...</option>
                                                    ) : uniqueProviders.length === 0 ? (
                                                        <option disabled>No providers found</option>
                                                    ) : (
                                                        uniqueProviders.map((p, i) => (
                                                            <option key={i} value={p}>{p}</option>
                                                        ))
                                                    )}
                                                </select>
                                            </div>

                                            {/* 2. Collector (Checkboxed) */}
                                            <div className={`space-y-2 transition-all duration-300 ${activeSections.collector ? 'opacity-100' : 'opacity-40 grayscale'}`}>
                                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2 cursor-pointer select-none">
                                                    <input type="checkbox" checked={activeSections.collector} onChange={() => dispatch(updateActiveSections({ collector: !activeSections.collector }))} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" />
                                                    <Users className="w-3 h-3" /> 2. Collector
                                                </label>
                                                <select disabled={!activeSections.collector} value={filters.collector} onChange={(e) => dispatch(updateFilters({ collector: e.target.value }))} className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed">
                                                    <option value="">Select Collector...</option>
                                                    {uniqueCollectors.map(c => <option key={c._id} value={c._id}>{c.username}</option>)}
                                                </select>
                                            </div>

                                            {/* 3. Hearing Report (Checkboxed) */}
                                            <div className={`space-y-2 md:col-span-2 bg-gray-50 p-4 rounded-xl border border-gray-100 transition-all duration-300 ${activeSections.hearing ? 'opacity-100' : 'opacity-40 grayscale'}`}>
                                                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2 mb-2 cursor-pointer select-none">
                                                    <input type="checkbox" checked={activeSections.hearing} onChange={() => dispatch(updateActiveSections({ hearing: !activeSections.hearing }))} className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500" />
                                                    <Calendar className="w-3 h-3" /> 3. Hearing Report (Date Range)
                                                </label>
                                                <div className="flex flex-col sm:flex-row gap-4">
                                                    <div className="flex-1"><span className="text-xs text-gray-400 mb-1 block">From</span><input disabled={!activeSections.hearing} type="date" value={filters.hearingStartDate} onChange={(e) => dispatch(updateFilters({ hearingStartDate: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" /></div>
                                                    <div className="flex-1"><span className="text-xs text-gray-400 mb-1 block">To</span><input disabled={!activeSections.hearing} type="date" value={filters.hearingEndDate} onChange={(e) => dispatch(updateFilters({ hearingEndDate: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" /></div>
                                                </div>
                                            </div>
                                            
                                            {/* 4. Settlement Rep (Self-Contained Checkbox) */}
                                            <div onClick={() => dispatch(updateFilters({ isSettled: !filters.isSettled }))} className={`cursor-pointer p-4 rounded-xl border flex items-center justify-between transition-all duration-300 ${filters.isSettled ? 'bg-pink-50 border-pink-200 shadow-sm opacity-100' : 'bg-white border-gray-200 hover:bg-gray-50 opacity-60 grayscale'}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-full ${filters.isSettled ? 'bg-pink-100 text-pink-600' : 'bg-gray-100 text-gray-400'}`}><Gavel className="w-5 h-5" /></div>
                                                    <div><h4 className={`font-bold text-sm ${filters.isSettled ? 'text-pink-800' : 'text-gray-700'}`}>4. Settlement Rep</h4><p className="text-xs text-gray-500">Only "SETTLED"</p></div>
                                                </div>
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center ${filters.isSettled ? 'bg-pink-500 border-pink-500' : 'border-gray-300'}`}>{filters.isSettled && <CheckSquare className="w-3.5 h-3.5 text-white" />}</div>
                                            </div>

                                            {/* 5. Offer Rep (Self-Contained Checkbox) */}
                                            <div onClick={() => dispatch(updateFilters({ isOffer: !filters.isOffer }))} className={`cursor-pointer p-4 rounded-xl border flex items-center justify-between transition-all duration-300 ${filters.isOffer ? 'bg-green-50 border-green-200 shadow-sm opacity-100' : 'bg-white border-gray-200 hover:bg-gray-50 opacity-60 grayscale'}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-full ${filters.isOffer ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}><DollarSign className="w-5 h-5" /></div>
                                                    <div><h4 className={`font-bold text-sm ${filters.isOffer ? 'text-green-800' : 'text-gray-700'}`}>5. Offer Rep</h4><p className="text-xs text-gray-500">Has Offer/Amount</p></div>
                                                </div>
                                                <div className={`w-5 h-5 rounded border flex items-center justify-center ${filters.isOffer ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>{filters.isOffer && <CheckSquare className="w-3.5 h-3.5 text-white" />}</div>
                                            </div>
                                        </div>

                                        <div className="pt-4 flex gap-3">
                                            {/* Button 1: Append (Merge) */}
                                            <button onClick={() => handleAdvancedFilter('append')} disabled={loading} className="flex-1 py-4 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 border border-gray-200">
                                                {loading ? 'Searching...' : <>
                                                    <PlusCircle className="w-5 h-5" /> 
                                                    <div className="flex flex-col items-start leading-tight">
                                                        <span>Append Results</span>
                                                        <span className="text-[10px] font-normal opacity-70">Add matches to current list</span>
                                                    </div>
                                                </>}
                                            </button>

                                            {/* Button 2: Replace (New Search) */}
                                            <button onClick={() => handleAdvancedFilter('replace')} disabled={loading} className="flex-1 py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-indigo-200">
                                                {loading ? 'Processing...' : <>
                                                    <RefreshCw className="w-5 h-5" /> 
                                                    <div className="flex flex-col items-start leading-tight">
                                                        <span>New Search (Replace)</span>
                                                        <span className="text-[10px] font-normal opacity-80">Discard old list, show new</span>
                                                    </div>
                                                </>}
                                            </button>
                                        </div>
                                    </div>

                                    {/* --- BASKET SUMMARY & NEXT STEP --- */}
                                    {foundRecords.length > 0 && (
                                        <div className="mt-8 animate-in fade-in duration-500">
                                            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-emerald-50 p-4 rounded-xl border border-emerald-100 shadow-sm">
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-emerald-100 p-2 rounded-full"><CheckSquare className="w-5 h-5 text-emerald-700" /></div>
                                                    <div>
                                                        <p className="font-bold text-gray-800">{foundRecords.length} Records Selected</p>
                                                        <p className="text-xs text-gray-500">Ready for export configuration</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={handleClearAll} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-5 h-5" /></button>
                                                    <button onClick={proceedToStep2} className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-emerald-700 transition-colors flex items-center gap-2 shadow-sm">Configure & Download <ArrowRight className="w-4 h-4" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* STEP 2: FIELD CONFIGURATION & PREVIEW */
                        <div className="flex flex-col md:flex-row h-full">
                            <div className="w-full md:w-1/3 lg:w-1/4 bg-white border-b md:border-b-0 md:border-r border-gray-200 flex flex-col max-h-60 md:max-h-full h-full">
                                <div className="p-4 border-b bg-gray-50 flex justify-between items-center shrink-0">
                                    <h4 className="font-semibold text-gray-700 flex items-center gap-2"><Filter className="w-4 h-4" /> Field Configuration</h4>
                                    <button onClick={toggleAllFields} className="text-xs text-blue-600 hover:underline font-medium">{selectedFields.length === ALL_FIELDS_FLAT.length ? 'Deselect All' : 'Select All'}</button>
                                </div>
                                <div className="overflow-y-auto p-4 space-y-4 custom-scrollbar">
                                    {Object.entries(FIELD_GROUPS).map(([groupName, fields]) => {
                                        const isExpanded = expandedGroups.includes(groupName);
                                        const groupKeys = fields.map(f => f.key);
                                        const someSelected = groupKeys.some(k => selectedFields.includes(k));
                                        const allSelected = groupKeys.every(k => selectedFields.includes(k));
                                        return (
                                            <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-100">
                                                    <div className="flex items-center gap-2 cursor-pointer select-none flex-1" onClick={() => toggleExpandGroup(groupName)}>
                                                        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                                                        <span className="text-sm font-semibold text-gray-700">{groupName}</span>
                                                    </div>
                                                    <div onClick={() => toggleGroupFields(groupName)} className="cursor-pointer p-1 hover:bg-gray-200 rounded">
                                                        {allSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : someSelected ? <div className="w-4 h-4 bg-blue-600 rounded-sm flex items-center justify-center"><div className="w-2 h-0.5 bg-white"></div></div> : <Square className="w-4 h-4 text-gray-300" />}
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className="p-2 bg-white space-y-1">
                                                        {fields.map(field => (
                                                            <label key={field.key} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors group">
                                                                <input type="checkbox" checked={selectedFields.includes(field.key)} onChange={() => toggleField(field.key)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer" />
                                                                <span className="text-sm text-gray-600 group-hover:text-gray-900">{field.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* PREVIEW TABLE */}
                            <div className="flex-1 flex flex-col h-full bg-gray-50 overflow-hidden">
                                <div className="p-4 bg-white border-b flex flex-col sm:flex-row justify-between items-start sm:items-center shadow-sm z-10 gap-3">
                                    <div className="flex flex-col">
                                        <h4 className="font-bold text-gray-800 text-base sm:text-lg">Review Export Data</h4>
                                        <p className="text-xs text-indigo-600 flex items-center gap-1"><Info className="w-3 h-3" /> Source: {lastFilterSummary || 'Custom Selection'}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs sm:text-sm text-gray-500">Selected: <span className="font-bold text-gray-900">{selectedRecordIds.length}</span> / {foundRecords.length}</span>
                                        <button onClick={toggleAllRecords} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md font-medium transition-colors border border-gray-200">{selectedRecordIds.length === foundRecords.length ? 'Deselect All' : 'Select All'}</button>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-auto p-2 sm:p-4">
                                    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left w-12"><input type="checkbox" checked={selectedRecordIds.length === foundRecords.length && foundRecords.length > 0} onChange={toggleAllRecords} className="w-4 h-4 text-blue-600 border-gray-300 rounded" /></th>
                                                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Patient</th>
                                                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Provider</th>
                                                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                                                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Bill</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {foundRecords.map(record => (
                                                        <tr key={record._id} className={`hover:bg-gray-50 transition-colors ${selectedRecordIds.includes(record._id) ? 'bg-indigo-50 hover:bg-indigo-100' : ''}`} onClick={() => toggleRecord(record._id)}>
                                                            <td className="px-6 py-4 whitespace-nowrap"><input type="checkbox" checked={selectedRecordIds.includes(record._id)} readOnly className="w-4 h-4 text-blue-600 border-gray-300 rounded" /></td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.ptName}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{record.provider}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap"><span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${record.caseStatus === 'SETTLED' ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'}`}>{record.caseStatus || 'N/A'}</span></td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">${record.bill?.toLocaleString() || '0.00'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                <div className="border-t px-4 py-3 sm:px-6 sm:py-4 bg-white flex justify-between items-center rounded-b-xl">
                    {step === 2 && (
                        <button onClick={() => dispatch(setStep(1))} className="text-indigo-600 hover:text-indigo-800 font-bold text-sm flex items-center gap-1 transition-colors">
                            <ChevronRight className="w-4 h-4 rotate-180" /> <span className="hidden sm:inline">Back to Filters</span>
                        </button>
                    )}
                    <div className="flex-1 flex justify-end gap-3">
                        <button onClick={handleCloseClick} className="px-5 py-2.5 bg-red-50 border border-red-200 rounded-lg text-red-600 font-bold hover:bg-red-100 transition-colors text-sm">Cancel</button>
                        {step === 2 && (
                            <button onClick={handleExport} disabled={selectedRecordIds.length === 0} className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center gap-2 shadow-sm transition-all hover:shadow-md disabled:bg-gray-300 disabled:shadow-none whitespace-nowrap">
                                <Download className="w-4 h-4" /> <span className="hidden sm:inline">Download Report</span><span className="sm:hidden">Export</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
     </div>
    );  
};

export default ExportReportModal;
